import React, { useMemo } from 'react';
import { Select, Button, Tag } from 'antd';
import { ExclamationCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { color } from '../../common';
import { getProductErrorMessages, matchesSelectedError } from '../../Util';
import './ErrorMessageFilter.css';

const { Option } = Select;

const styles = {
    container: {
        background: '#fff',
        padding: '12px 16px',
        borderRadius: '6px',
        margin: '0 10px 10px 10px',
        border: `1px solid ${color.grayContainer}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
    },
    title: {
        fontWeight: '600',
        color: color.primaryTextColor,
        margin: 0,
        fontSize: '14px',
    },
    filterSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    selectContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontWeight: '500',
        color: color.grayDarkest,
        fontSize: '13px',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    counter: {
        color: color.primaryTextColor,
        fontWeight: '500',
        fontSize: '12px',
    },
};



const ErrorMessageFilter = ({
    products = [],
    selectedErrorMessages = [],
    onSelectedErrorMessagesChange,
}) => {
    const availableErrorMessages = useMemo(() => {
        const errorMessages = new Set();

        products.forEach(product => {
            getProductErrorMessages(product).forEach(err => {
                if (err.clean) {
                    errorMessages.add(err.clean);
                }
            });
        });

        return Array.from(errorMessages).sort();
    }, [products]);

    const filteredProductsWithErrors = useMemo(() => {
        if (selectedErrorMessages.length === 0) return [];

        return products.filter(product => {
            return getProductErrorMessages(product).some(err =>
                selectedErrorMessages.some(selectedMsg => matchesSelectedError(err, selectedMsg))
            );
        });
    }, [products, selectedErrorMessages]);

    const errorCounts = useMemo(() => {
        const counts = {};

        availableErrorMessages.forEach(errorMsg => {
            counts[errorMsg] = products.filter(product => {
                return getProductErrorMessages(product).some(err => matchesSelectedError(err, errorMsg));
            }).length;
        });

        return counts;
    }, [products, availableErrorMessages]);

    const handleSelectChange = (values) => {
        onSelectedErrorMessagesChange(values);
    };

    const handleClearFilters = () => {
        onSelectedErrorMessagesChange([]);
    };

    const hasProductsWithErrors = useMemo(() => {
        return products.some(product => getProductErrorMessages(product).length > 0);
    }, [products]);

    if (!hasProductsWithErrors || availableErrorMessages.length === 0) {
        return null;
    }

    return (
        <div className="error-filter-container" style={styles.container}>
            <div style={styles.header}>
                <FilterOutlined style={{ color: color.primaryTextColor }} />
                <h4 style={styles.title}>Filtrar produtos por tipo de erro</h4>
            </div>

            <div style={styles.filterSection}>
                <div style={styles.selectContainer}>
                    <label style={styles.label} id="error-filter-label">
                        Selecione os tipos de erro para filtrar:
                    </label>
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="Escolha as mensagens de erro..."
                        value={selectedErrorMessages}
                        onChange={handleSelectChange}
                        style={{ width: '100%' }}
                        maxTagCount="responsive"
                        showSearch
                        aria-labelledby="error-filter-label"
                        aria-describedby="error-filter-description"
                        filterOption={(input, option) =>
                            option.value.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {availableErrorMessages.map(errorMsg => {
                            return (
                                <Option key={errorMsg} value={errorMsg} title={errorMsg}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <span style={{ 
                                            flex: 1, 
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                        }}>
                                            {errorMsg}
                                        </span>
                                        <Tag
                                            color={errorCounts[errorMsg] > 0 ? 'red' : 'default'}
                                            style={{ 
                                                fontSize: '11px', 
                                                flexShrink: 0,
                                            }}
                                        >
                                            {errorCounts[errorMsg] || 0}
                                        </Tag>
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                    <small id="error-filter-description" style={{ color: color.grayMid, fontSize: '11px' }}>
                        Selecione uma ou mais mensagens para marcar ou remover produtos com esses erros específicos.
                    </small>
                </div>

                {selectedErrorMessages.length > 0 && (
                    <>
                        <div style={styles.counter}>
                            <ExclamationCircleOutlined style={{ color: color.dangerColor, marginRight: '4px' }} />
                            {filteredProductsWithErrors.length} produto(s) com erro(s) selecionado(s)
                        </div>

                        <div style={styles.actionButtons}>
                            <Button
                                type="link"
                                size="small"
                                onClick={handleClearFilters}
                                style={{ padding: '0 8px', fontSize: '12px' }}
                            >
                                Limpar Filtros
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ErrorMessageFilter;
